import {htmlToTokens, isSelfEnclosingTag} from 'app/string/htmldiff';

import {
  attachToNewParent,
  wrapHtmlNodeInSpanWithoutParent,
  wrapHtmlNodeInSpanWithParent,
  wrapTextNodeInSpanWithoutParent,
  wrapTextNodeInSpanWithParent,
} from './nodes';

import type {
  HtmlDocType,
  HtmlDocTypeWithoutParent,
  HtmlDocTypeWithParent,
  NodeType,
  TextNodeType,
  TextNodeTypeWithoutParent,
  TextNodeTypeWithParent,
} from '../nodeProcessors/node-processors-types';

type RemoveTextNodeWithoutParentType<T> = T extends {parent: null}
  ? HtmlDocTypeWithoutParent
  : TextNodeTypeWithParent | HtmlDocTypeWithParent;

function wrapTextNodeWithSiblingsInSpan(
  node: TextNodeTypeWithParent | TextNodeTypeWithoutParent
): RemoveTextNodeWithoutParentType<typeof node> {
  if (node.parent == null) {
    return wrapTextNodeInSpanWithoutParent(node);
  }
  if (node.prev != null || node.next != null) {
    return wrapTextNodeInSpanWithParent(node);
  }
  return node as TextNodeTypeWithParent;
}

function wrapEnterWithSiblingsInSpan(
  node: HtmlDocTypeWithParent | HtmlDocTypeWithoutParent
): typeof node {
  if (node.name === 'br') {
    if (node.parent == null) {
      return wrapHtmlNodeInSpanWithoutParent(node);
    }
    if (node.prev != null || node.next != null) {
      return wrapHtmlNodeInSpanWithParent(node);
    }
  }

  return node;
}

export function reformatHTMLParserNodes(
  nodes: Array<TextNodeType | HtmlDocType>
) {
  const reformattedHtmlNodes: Array<
    TextNodeTypeWithParent | HtmlDocTypeWithParent | HtmlDocTypeWithoutParent
  > = [];
  nodes.forEach((node) => {
    if (node.type === 'text') {
      reformattedHtmlNodes.push(wrapTextNodeWithSiblingsInSpan(node));
    } else {
      let htmlNode = wrapEnterWithSiblingsInSpan(node);
      if (htmlNode.children.length > 0) {
        // @ts-expect-error I couldn't fix up the types here, so I'm leaving this as-is
        htmlNode.children = reformatHTMLParserNodes(htmlNode.children);
      }
      reformattedHtmlNodes.push(htmlNode);
    }
  });

  return reformattedHtmlNodes;
}

function isHtmlDocTypeNode(
  node: null | HtmlDocType | TextNodeType
): node is HtmlDocType {
  return node != null && node.type === 'tag';
}

function isImageNode(
  node: null | HtmlDocType | TextNodeType
): node is HtmlDocType {
  return isHtmlDocTypeNode(node) && node.name === 'img';
}

function isGroupableImageNode(
  node: null | HtmlDocType | TextNodeType
): node is HtmlDocType {
  return (
    isImageNode(node) && !node.attribs?.['class']?.includes('fr-no-carousel')
  );
}

function isCarouselNode(
  node: null | HtmlDocType | TextNodeType
): node is HtmlDocType {
  return isHtmlDocTypeNode(node) && node.name === 'carousel';
}

function generateCarouselNode(node: HtmlDocType) {
  const carousel =
    node.parent == null
      ? wrapHtmlNodeInSpanWithoutParent(node)
      : wrapHtmlNodeInSpanWithParent(node);

  carousel.attribs = {};
  carousel.name = 'carousel';

  return carousel;
}

export function groupDocumentImages(nodes: Array<TextNodeType | HtmlDocType>) {
  const nodesWithGroupedImages: Array<TextNodeType | HtmlDocType> = [];
  nodes.forEach((node) => {
    if (node.type === 'text') {
      nodesWithGroupedImages.push(node);
      return;
    }
    let htmlNode = node;
    if (isGroupableImageNode(htmlNode)) {
      if (isCarouselNode(htmlNode.prev)) {
        // Previous node is a carousel, and current node (htmlNode) is an image.
        // We should append this node to the children of carousel node and early-return.
        attachToNewParent(htmlNode, htmlNode.prev);
        return;
      }
      if (isGroupableImageNode(htmlNode.next)) {
        // Next node is an image node, and previous node is not an image node. This
        // means that we detected the begining of image series that we need to wrap
        // into a carousel

        htmlNode = generateCarouselNode(htmlNode); // old htmlNode will be put into
        // the carousel
      }
    }
    if (htmlNode.name !== 'carousel' && htmlNode.children.length > 0) {
      // @ts-expect-error I couldn't fix up the types here, so I'm leaving this as-is
      htmlNode.children = groupDocumentImages(htmlNode.children);
    }
    nodesWithGroupedImages.push(htmlNode);
  });
  return nodesWithGroupedImages;
}

export function attachHtmldiffIndex(nodes: Array<NodeType>, currentIndex = 0) {
  nodes.forEach((node) => {
    if (isHtmlDocTypeNode(node)) {
      node.attribs['data-htmldiff-start'] = `${currentIndex}`;

      if (
        node.attribs['data-node-virtual'] !== 'virtual' &&
        node.name !== 'del' &&
        node.attribs['data-diff-node'] !== 'del' &&
        node.name !== 'ins'
      ) {
        currentIndex += 1;
      }

      if (
        node.children.length > 0 &&
        node.attribs['data-diff-node'] !== 'del' &&
        node.name !== 'del'
      ) {
        currentIndex = attachHtmldiffIndex(node.children, currentIndex);
      }

      if (
        node.attribs['data-node-virtual'] !== 'virtual' &&
        node.name !== 'del' &&
        node.attribs['data-diff-node'] !== 'del' &&
        node.name !== 'ins'
      ) {
        if (!isSelfEnclosingTag(node.name)) {
          currentIndex += 1;
        }
      }

      node.attribs['data-htmldiff-end'] = `${currentIndex}`;
    } else {
      currentIndex += htmlToTokens(node.data.replace(/&/g, '&amp;')).length;
    }
  });

  return currentIndex;
}
